import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { createPortal } from 'react-dom';

const API_BASE = process.env.REACT_APP_API_URL;

function resolveUrl(url) {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${API_BASE}${url}`;
}

function TagBadge({ tag }) {
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700,
      background: 'rgba(70,187,137,0.14)', border: '1px solid rgba(70,187,137,0.35)',
      color: 'var(--ni-green)', whiteSpace: 'nowrap', letterSpacing: '0.04em',
      textTransform: 'uppercase',
    }}>
      {tag}
    </span>
  );
}

function ImageCard({ entry, onClick }) {
  const date = entry.date ? new Date(entry.date).toLocaleDateString('en-CA') : '';
  return (
    <div
      onClick={onClick}
      style={{
        background: 'rgba(17,22,20,0.94)',
        border: '1px solid var(--ni-border-soft)',
        borderRadius: 12,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'border-color 0.15s, transform 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(70,187,137,0.45)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--ni-border-soft)'; e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <div style={{ width: '100%', paddingTop: '66%', position: 'relative', background: '#0a0f0c' }}>
        <img
          src={resolveUrl(entry.url)}
          alt={entry.name || ''}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>
      <div style={{ padding: '10px 12px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
          {entry.tags.map(t => <TagBadge key={t} tag={t} />)}
        </div>
        {entry.caption && (
          <p style={{ margin: '0 0 6px', fontSize: 12, color: 'var(--ni-text-soft)', lineHeight: 1.4 }}>
            {entry.caption}
          </p>
        )}
        <div style={{ fontSize: 11, color: 'var(--ni-text-muted)' }}>
          {entry.serialNumber && <span>{entry.serialNumber}</span>}
          {entry.rmaId && <span style={{ marginLeft: 8 }}>{entry.rmaId}</span>}
          {date && <span style={{ marginLeft: 8 }}>{date}</span>}
          {entry.archived && <span style={{ marginLeft: 8, color: 'var(--ni-green)' }}>archived</span>}
        </div>
      </div>
    </div>
  );
}

function Lightbox({ entry, onClose, onPrev, onNext, hasMultiple }) {
  useEffect(() => {
    const handler = e => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, onPrev, onNext]);

  return createPortal(
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      background: 'rgba(7,9,8,0.97)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <button onClick={e => { e.stopPropagation(); onClose(); }} style={closeBtn}>✕</button>
      {hasMultiple && <button onClick={e => { e.stopPropagation(); onPrev(); }} style={{ ...navBtn, left: 16 }}>‹</button>}

      <div onClick={e => e.stopPropagation()} style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        maxWidth: 860, width: '100%', padding: '48px 20px 24px',
      }}>
        <img
          src={resolveUrl(entry.url)}
          alt={entry.name || ''}
          style={{ maxWidth: '100%', maxHeight: '60vh', borderRadius: 10, objectFit: 'contain', boxShadow: '0 8px 40px rgba(0,0,0,0.7)' }}
        />
        <div style={{ marginTop: 16, textAlign: 'center', maxWidth: 600 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', marginBottom: 10 }}>
            {entry.tags.map(t => <TagBadge key={t} tag={t} />)}
          </div>
          {entry.caption && <p style={{ color: 'var(--ni-text-soft)', fontSize: 14, margin: '0 0 8px' }}>{entry.caption}</p>}
          <div style={{ fontSize: 12, color: 'var(--ni-text-muted)' }}>
            {entry.serialNumber} · {entry.rmaId} · {entry.date ? new Date(entry.date).toLocaleDateString('en-CA') : ''}
          </div>
        </div>
      </div>

      {hasMultiple && <button onClick={e => { e.stopPropagation(); onNext(); }} style={{ ...navBtn, right: 16 }}>›</button>}
    </div>,
    document.body
  );
}

export default function TrainingLibrary() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/training/images`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data.ok) setImages(data.images);
      } catch (err) {
        console.error('[training] fetch error', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [getToken]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return images;
    return images.filter(img =>
      img.tags.some(t => t.toLowerCase().includes(q)) ||
      (img.caption || '').toLowerCase().includes(q) ||
      (img.serialNumber || '').toLowerCase().includes(q) ||
      (img.rmaId || '').toLowerCase().includes(q) ||
      (img.productNumber || '').toLowerCase().includes(q)
    );
  }, [images, search]);

  const allTags = useMemo(() => {
    const counts = {};
    images.forEach(img => img.tags.forEach(t => { counts[t] = (counts[t] || 0) + 1; }));
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [images]);

  return (
    <div className="ni-page">
      <div className="ni-shell">
        {/* Header */}
        <header className="ni-header">
          <div className="ni-brand">
            <img src="/north-invent-logo.svg" alt="North Invent" className="ni-brand-logo" />
            <div className="ni-brand-copy">
              <span className="ni-eyebrow">Service</span>
              <span className="ni-brand-title">Training Library</span>
              <span className="ni-brand-subtitle">
                {loading ? 'Loading…' : `${filtered.length} categorized image${filtered.length !== 1 ? 's' : ''}`}
              </span>
            </div>
          </div>
          <div className="ni-toolbar">
            <button className="ni-button ni-button--ghost" onClick={() => navigate('/')}>
              ← Back to Main
            </button>
          </div>
        </header>

        {/* Search + tag cloud */}
        <div className="ni-panel">
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by tag, serial number, RMA ID, caption…"
            className="ni-input"
            style={{ maxWidth: 480 }}
          />
          {allTags.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 14 }}>
              {allTags.map(([tag, count]) => (
                <button
                  key={tag}
                  onClick={() => setSearch(search === tag ? '' : tag)}
                  style={{
                    padding: '4px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
                    fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
                    border: `1px solid ${search === tag ? 'rgba(70,187,137,0.5)' : 'var(--ni-border-soft)'}`,
                    background: search === tag ? 'rgba(70,187,137,0.14)' : 'rgba(255,255,255,0.03)',
                    color: search === tag ? 'var(--ni-green)' : 'var(--ni-text-muted)',
                    transition: 'all 0.15s',
                  }}
                >
                  {tag} <span style={{ opacity: 0.6, fontWeight: 400 }}>({count})</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        {loading && (
          <div className="ni-panel ni-empty">Loading images…</div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="ni-panel ni-empty">
            {images.length === 0
              ? 'No categorized images yet. Open an RMA case, click a submission photo, and add tags.'
              : 'No images match your search.'}
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 16,
          }}>
            {filtered.map((entry, idx) => (
              <ImageCard key={`${entry.url}-${idx}`} entry={entry} onClick={() => setLightboxIdx(idx)} />
            ))}
          </div>
        )}
      </div>

      {lightboxIdx !== null && (
        <Lightbox
          entry={filtered[lightboxIdx]}
          hasMultiple={filtered.length > 1}
          onClose={() => setLightboxIdx(null)}
          onPrev={() => setLightboxIdx(i => (i - 1 + filtered.length) % filtered.length)}
          onNext={() => setLightboxIdx(i => (i + 1) % filtered.length)}
        />
      )}
    </div>
  );
}

const closeBtn = {
  position: 'fixed', top: 16, right: 20,
  background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(147,149,151,0.3)',
  color: 'var(--ni-text)', fontSize: 20, cursor: 'pointer', zIndex: 1,
  borderRadius: 8, padding: '4px 10px', lineHeight: 1,
};
const navBtn = {
  position: 'fixed', background: 'rgba(70,187,137,0.12)', border: '1px solid rgba(70,187,137,0.3)',
  color: 'var(--ni-green)', fontSize: 36, cursor: 'pointer', borderRadius: 8,
  padding: '0.25rem 0.6rem', top: '50%', transform: 'translateY(-50%)',
};
